import React, { useState, useEffect, useRef } from "react";
import { DollarSign, Clock, CheckCircle, XCircle, User, Calendar, Mail, Wallet, Building2 } from "lucide-react";
import { format } from "date-fns";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import {
  adminAgencyPayoutsService,
  AgencyPayoutRequest,
  AgencyPayoutSearchResponse,
} from "../../services/admin/agencyPayouts.admin.service";
import { toast } from "react-hot-toast";

// Stat card skeleton
function StatCardSkeleton() {
  return (
    <div className="bg-transparent border border-border rounded-xl animate-pulse p-6">
      <div className="flex items-center justify-between space-x-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-muted rounded w-1/2"></div>
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
        <div className="h-12 w-12 rounded-2xl border border-border bg-transparent"></div>
      </div>
    </div>
  );
}

// Stat card component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  formatter?: (value: number) => string;
}

function StatCard({ title, value, icon, color, formatter }: StatCardProps) {
  const formattedValue =
    formatter && typeof value === "number"
      ? formatter(value)
      : value.toString();
  const isWarm = color === "bg-orange-100 dark:bg-orange-500/15";

  return (
    <div className="bg-transparent border border-border rounded-xl transition-all duration-300 hover:border-[#d7785b]/40">
      <div className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold text-foreground">
              {formattedValue}
            </p>
          </div>
          <div
            className={`h-12 w-12 rounded-2xl flex items-center justify-center border ${
              isWarm
                ? "border-[#d7785b]/20 bg-transparent"
                : "border-border bg-transparent"
            }`}
          >
            <div className={isWarm ? "text-[#d7785b]" : "text-muted-foreground"}>
              {icon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminAgencyPayouts() {
  const [payouts, setPayouts] = useState<AgencyPayoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    totalPendingRequests: 0,
    totalPaidRequests: 0,
    totalRequests: 0,
    totalPendingAmount: 0,
    totalPaidAmount: 0,
    totalAmount: 0,
    averagePayoutPerRequest: 0,
  });
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'PAID'>('PENDING');
  const [showModal, setShowModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<AgencyPayoutRequest | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Fetch agency payouts
  const fetchPayouts = async (p: number = page, status: 'PENDING' | 'PAID' = statusFilter, size: number = pageSize) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      const res: AgencyPayoutSearchResponse = await adminAgencyPayoutsService.getAgencyPayoutRequests(p, size, status);
      setPayouts(res.data);
      setPage(res.pagination.page);
      setTotal(res.pagination.total);
      setTotalPages(res.pagination.totalPages);
    } catch (error) {
      console.error("Failed to fetch agency payouts:", error);
      toast.error("Failed to fetch agency payouts");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAgencyPayoutsService.getAgencyPayoutStats();
      setStats(response);
    } catch (error) {
      console.error("Failed to fetch payout stats:", error);
    }
  }

  // Handle view payout
  const handleViewPayout = (payout: AgencyPayoutRequest) => {
    setSelectedPayout(payout);
    setSelectedPaymentMethod("");
    setReferenceNumber("");
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedPayout(null);
    setSelectedPaymentMethod("");
    setReferenceNumber("");
  };

  // Handle mark as paid
  const handleMarkAsPaid = async () => {
    if (!selectedPayout || !selectedPaymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    try {
      setModalLoading(true);
      await adminAgencyPayoutsService.markAgencyPayoutPaid(
        selectedPayout.id,
        selectedPaymentMethod,
        referenceNumber || undefined
      );
      toast.success("Agency payout marked as paid successfully");
      closeModal();
      fetchPayouts(); // Refresh the list
    } catch (error) {
      console.error("Failed to mark payout as paid:", error);
      toast.error("Failed to mark payout as paid");
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts(1);
    fetchStats();
  }, []);

  
  // Table columns
  const columns: ColumnDef<AgencyPayoutRequest>[] = [
    {
      id: "agency",
      header: "Agency",
      meta: { width: "w-48" },
      cell: ({ row }) => (
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
            <Building2 className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="font-medium text-foreground truncate">
            {`${row.original.agency.firstName} ${row.original.agency.lastName}`}
          </div>
        </div>
      ),
    },
    {
      id: "email",
      header: "Email",
      meta: { width: "w-56" },
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground truncate">
          {row.original.agency.email}
        </div>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm font-medium text-foreground">
          <DollarSign className="w-3 h-3 text-muted-foreground" />
          {formatCurrency(row.original.amount)}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            row.original.status === "PENDING"
              ? "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300"
              : row.original.status === "PAID"
              ? "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300"
              : "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300"
          }`}
        >
          {row.original.status === "PENDING" && (
            <Clock className="w-3 h-3 mr-1" />
          )}
          {row.original.status === "PAID" && (
            <CheckCircle className="w-3 h-3 mr-1" />
          )}
          {row.original.status}
        </span>
      ),
    },
    {
      id: "paymentMethods",
      header: "Payment Methods",
      meta: { width: "w-40" },
      cell: ({ row }) =>
        statusFilter === 'PENDING' ? (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Wallet className="w-3 h-3 text-muted-foreground" />
            <span className="text-foreground">
              {row.original.paymentInfos.length > 0
                ? `${row.original.paymentInfos.length} method${row.original.paymentInfos.length > 1 ? 's' : ''}`
                : 'No methods'}
            </span>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {row.original.paymentDetails?.paymentMethodName || '—'}
          </div>
        ),
    },
    {
      id: "reference",
      header: "Reference",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground truncate">
          {statusFilter === 'PAID' ? (row.original.referenceNumber || '—') : '—'}
        </div>
      ),
    },
    {
      id: "createdAt",
      header: "Created",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Calendar className="w-3 h-3" style={{ color: "#d7785b" }} />
          {format(new Date(row.original.createdAt), "MMM dd, yyyy")}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-foreground mb-2">
            Agency Payout Requests
          </h1>
          <p className="text-muted-foreground">
            Manage agency payout requests and payments
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Total Pending Requests"
              value={stats.totalPendingRequests}
              icon={<DollarSign className="w-5 h-5 text-foreground" />}
              color="bg-muted"
            />
            <StatCard
              title="Pending Amount"
              value={stats.totalPendingAmount}
              icon={<Clock className="w-5 h-5" style={{ color: "#d7785b" }} />}
              color="bg-orange-100 dark:bg-orange-500/15"
              formatter={formatCurrency}
            />
            <StatCard
              title="Total Paid Amount"
              value={stats.totalPaidAmount}
              icon={<Building2 className="w-5 h-5 text-foreground" />}
              color="bg-muted"
            />
          </>
        )}
      </div>

      {/* Payouts Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        <UnderlineTabs<'PENDING' | 'PAID'>
          className="px-5 pt-2"
          accent="terracotta"
          tabs={[
            { value: 'PENDING', label: 'Pending' },
            { value: 'PAID', label: 'Paid' },
          ]}
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value);
            fetchPayouts(1, value);
          }}
        />
        <DataTable
          data={payouts}
          columns={columns}
          isLoading={loading}
          emptyText={statusFilter === 'PENDING' ? 'No pending agency payout requests' : 'No paid agency payouts found'}
          onRowClick={handleViewPayout}
        />
        {totalPages > 1 && (
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
              fetchPayouts(1, statusFilter, size);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={(p) => fetchPayouts(p, statusFilter)}
            noun="payouts"
          />
        )}
      </div>

      {/* Payout Details Modal */}
      {showModal && selectedPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">
                  Agency Payout Request Details
                </h2>
                <button
                  onClick={closeModal}
                  className="text-muted-foreground hover:text-muted-foreground"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Agency Info */}
              <div className="bg-muted rounded-lg p-4">
                <h3 className="text-lg font-medium text-foreground mb-3">
                  Agency Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Name:</span>
                    <span className="text-sm font-medium text-foreground">
                      {`${selectedPayout.agency.firstName} ${selectedPayout.agency.lastName}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="text-sm font-medium text-foreground">
                      {selectedPayout.agency.email}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payout Details */}
              <div className="bg-muted rounded-lg p-4">
                <h3 className="text-lg font-medium text-foreground mb-3">
                  Payout Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Amount:</span>
                    <span className="text-sm font-medium text-foreground">
                      {formatCurrency(selectedPayout.amount)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Created:</span>
                    <span className="text-sm font-medium text-foreground">
                      {format(new Date(selectedPayout.createdAt), "MMM dd, yyyy")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <span className={`text-sm font-medium ${
                      selectedPayout.status === "PENDING" ? "text-yellow-600 dark:text-yellow-400" : "text-green-600 dark:text-green-400"
                    }`}>
                      {selectedPayout.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              {statusFilter === 'PENDING' && selectedPayout.paymentInfos.length > 0 && (
                <div className="bg-muted rounded-lg p-4">
                  <h3 className="text-lg font-medium text-foreground mb-3">
                    Agency Payment Methods
                  </h3>
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-muted-foreground">
                      Select Payment Method:
                    </label>
                    <select
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b] focus:border-transparent"
                    >
                      <option value="">Choose a payment method...</option>
                      {selectedPayout.paymentInfos.map((paymentInfo) => (
                        <option key={paymentInfo.id} value={paymentInfo.id}>
                          {paymentInfo.paymentMethod.name}
                        </option>
                      ))}
                    </select>

                    {/* Show selected payment method details */}
                    {selectedPaymentMethod && (
                      <div className="mt-4 p-3 bg-card rounded border">
                        {(() => {
                          const selectedInfo = selectedPayout.paymentInfos.find(
                            (info) => info.id === selectedPaymentMethod
                          );
                          if (!selectedInfo) return null;

                          return (
                            <div>
                              <h4 className="font-medium text-foreground mb-2">
                                {selectedInfo.paymentMethod.name} Details:
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {selectedInfo.paymentMethod.fields.map((field) => (
                                  <div key={field.id} className="text-sm">
                                    <span className="text-muted-foreground">{field.label}:</span>
                                    <span className="ml-2 font-medium">
                                      {selectedInfo.fieldData[field.fieldName] || "N/A"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Paid Info */}
              {statusFilter === 'PAID' && (
                <div className="bg-muted rounded-lg p-4">
                  <h3 className="text-lg font-medium text-foreground mb-3">Payment Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Method:</span>
                      <span className="ml-2 font-medium">{selectedPayout.paymentDetails?.paymentMethodName || '—'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Reference #:</span>
                      <span className="ml-2 font-medium">{selectedPayout.referenceNumber || '—'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Paid At:</span>
                      <span className="ml-2 font-medium">{selectedPayout.paidAt ? format(new Date(selectedPayout.paidAt), 'MMM dd, yyyy') : '—'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Reference Number */}
              {statusFilter === 'PENDING' && (
              <div className="bg-muted rounded-lg p-4">
                <h3 className="text-lg font-medium text-foreground mb-3">
                  Payment Reference
                </h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Reference Number (Optional):
                  </label>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="Enter payment reference number..."
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b] focus:border-transparent"
                  />
                </div>
              </div>
              )}
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-muted-foreground bg-muted rounded-lg hover:bg-muted transition-colors"
                >
                  Close
                </button>
                {statusFilter === 'PENDING' && (
                  <button
                    onClick={handleMarkAsPaid}
                    disabled={!selectedPaymentMethod || modalLoading}
                    className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: selectedPaymentMethod && !modalLoading ? "#d7785b" : "#9ca3af"
                    }}
                  >
                    {modalLoading ? "Processing..." : "Mark as Paid"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}